import { useMemo } from "react";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { resolvePreFlightScriptsAccess } from "@/features/auth/lib/assert-pre-flight-scripts-access";
import { SystemPermissions, type SystemPermissionKey, type PermissionViewModel } from "@nexus/contracts";

/**
 * Derives PermissionViewModel for the active tenant.
 *
 * Compares SystemPermissions (compile-time truth) against the granted set
 * for the active tenant. Missing → isOn: false, present → isOn: true.
 *
 * Recomputes only when activeTenantId or tenants change.
 */
export function usePermissions(): Record<SystemPermissionKey, PermissionViewModel> {
  const tenants = useAuth((s) => s.tenants);
  const activeTenantId = useAuth((s) => s.activeTenantId);

  return useMemo(() => {
    const activeTenant = tenants.find((t) => t.id === activeTenantId);
    const granted = activeTenant?.permissions ?? [];

    const result = {} as Record<SystemPermissionKey, PermissionViewModel>;
    for (const [key, def] of Object.entries(SystemPermissions) as [SystemPermissionKey, typeof SystemPermissions[SystemPermissionKey]][]) {
      const isOn = granted.some(
        (p) => p.type === def.type && p.method === def.method && p.route === def.route,
      );
      result[key] = { type: def.type, method: def.method, route: def.route, isOn };
    }
    return result;
  }, [tenants, activeTenantId]);
}

/**
 * Returns true if the current user belongs to at least one SUPER_TENANT.
 */
export function useIsSuperTenant(): boolean {
  const tenants = useAuth((s) => s.tenants);
  return useMemo(() => tenants.some((t) => t.type === "SUPER_TENANT"), [tenants]);
}

/** True only while the selected workspace itself is the SUPER_TENANT. */
export function useIsActiveSuperTenant(): boolean {
  const tenants = useAuth((s) => s.tenants);
  const activeTenantId = useAuth((s) => s.activeTenantId);
  return useMemo(
    () => tenants.find((tenant) => tenant.id === activeTenantId)?.type === "SUPER_TENANT",
    [tenants, activeTenantId],
  );
}

/** True when the active tenant is a marketplace production partner org (legacy type check). */
export function useIsPartnerTenant(): boolean {
  const tenants = useAuth((s) => s.tenants);
  const activeTenantId = useAuth((s) => s.activeTenantId);
  return useMemo(
    () => tenants.find((t) => t.id === activeTenantId)?.type === "PARTNER",
    [tenants, activeTenantId],
  );
}

/** True when the user is in partner portal mode (marketplace.partner page grant). */
export function useIsPartnerPortalUser(): boolean {
  const tenants = useAuth((s) => s.tenants);
  const activeTenantId = useAuth((s) => s.activeTenantId);
  return useMemo(
    () => resolvePreFlightScriptsAccess(tenants, activeTenantId) === "partner-review",
    [tenants, activeTenantId],
  );
}

/**
 * Returns true when `accessId` is one of the current user's own access groups —
 * mirrors the API's `requireGrantablePermissions` self-edit block (PUT
 * /access/permissions) so the UI can disable the action instead of letting the
 * user hit a 403. Super-tenant members bypass that backend check, so this
 * always returns false for them.
 */
export function useIsOwnAccessGroup(accessId: string): boolean {
  const userAcs = useAuth((s) => s.user?.acs);
  const isSuperTenant = useIsSuperTenant();
  return !isSuperTenant && !!userAcs?.includes(accessId);
}

/**
 * Gates a single permission the way every Identity Hub action check does:
 * granted for the active tenant, OR the user is a super-tenant member (super
 * tenants bypass permission checks the same way the API's route guards do).
 * Replaces the repeated `perms.X.isOn || isSuperTenant` inline ternary.
 *
 * `perm` may be undefined when `SystemPermissions` in the running bundle is
 * stale (e.g. Vite optimized-deps cache ahead of a contracts rebuild).
 */
export function useIamPermission(perm: PermissionViewModel | undefined): boolean {
  const isSuperTenant = useIsSuperTenant();
  return perm?.isOn === true || isSuperTenant;
}

/**
 * Same grant check as `useIamPermission`, but against a specific tenant rather
 * than the sidebar tenant. Identity Hub org settings edit a named org that can
 * differ from the active workspace.
 */
export function useIamPermissionForTenant(tenantId: string, key: SystemPermissionKey): boolean {
  const tenants = useAuth((s) => s.tenants);
  const isSuperTenant = useIsSuperTenant();

  return useMemo(() => {
    if (isSuperTenant) return true;
    const def = SystemPermissions[key];
    const tenant = tenants.find((t) => t.id === tenantId);
    if (!tenant) return false;
    return tenant.permissions.some(
      (p) => p.type === def.type && p.method === def.method && p.route === def.route,
    );
  }, [tenants, tenantId, key, isSuperTenant]);
}
